import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useSalonStore } from "@/lib/store/salon";
import { useSetupWorkerAccount } from "../hooks/use-invitations";
import type { TeamMember } from "../types";

// Setup account form schema
const setupAccountSchema = z.object({
  email: z.string().email("Please enter a valid email"),
});

type SetupAccountFormData = z.infer<typeof setupAccountSchema>;

interface SetupAccountDialogProps {
  worker: TeamMember;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function SetupAccountDialog({
  worker,
  isOpen,
  onClose,
  onSuccess,
}: SetupAccountDialogProps) {
  const [showSuccess, setShowSuccess] = useState(false);
  const [invitationLink, setInvitationLink] = useState<string>("");

  const [setupWorkerAccount, { loading: isLoading }] = useSetupWorkerAccount();
  const currentSalon = useSalonStore((state) => state.currentSalon);

  const form = useForm<SetupAccountFormData>({
    resolver: zodResolver(setupAccountSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleClose = () => {
    form.reset();
    setShowSuccess(false);
    setInvitationLink("");
    onClose();
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationLink);
      toast.success("Invitation link copied to clipboard");
    } catch (_error) {
      toast.error("Failed to copy link");
    }
  };

  const handleSubmit = async (data: SetupAccountFormData) => {
    if (!currentSalon?.id) {
      toast.error("No salon selected. Please refresh the page and try again.");
      return;
    }

    try {
      const result = await setupWorkerAccount({
        variables: {
          salonId: currentSalon.id,
          workerId: worker.workerId,
          input: {
            email: data.email,
          },
        },
      });

      const setupResult = result.data?.setupWorkerAccount;
      if (setupResult) {
        // Extract invitation link from the result if available
        const link = setupResult.invitationLink || "";
        setInvitationLink(link);
        setShowSuccess(true);
        onSuccess?.();
      }
    } catch (error: any) {
      console.error("Failed to setup account:", error);

      let errorMessage = "An unexpected error occurred. Please try again.";
      if (error?.graphQLErrors?.length > 0) {
        errorMessage = error.graphQLErrors[0].message;
      } else if (error?.networkError) {
        errorMessage = "Network error. Please check your connection and try again.";
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  if (showSuccess) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invitation Sent!</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <title>Success</title>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">
                An invitation has been sent. Please check your email to complete the setup.
              </p>
            </div>

            {invitationLink && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Invitation Link</label>
                <div className="flex gap-2">
                  <Input value={invitationLink} readOnly className="flex-1" />
                  <Button onClick={handleCopyLink} variant="outline">
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  You can share this link directly with the team member
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Setup Account for {worker.worker?.displayName ?? ""}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Enter the team member's email address to send them an invitation.
          </p>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email Address</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        placeholder="e.g., john@example.com"
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Sending..." : "Send Invitation"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
